import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Box, Grid, Paper, TextField, Button, FormControlLabel, Switch, Checkbox,
  Alert, CircularProgress, useMediaQuery, useTheme
} from '@mui/material';
import { Category, Save } from '@mui/icons-material';
import { MenuInicial } from './MenuInicial';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL, GRUPO_GET, GRUPO_ALTA, GRUPO_ACTUALIZAR } from '../services/apiConfig';
import TituloPagina from './utils/TituloPagina.jsx';
import VolverBreadcrumb from './utils/VolverBreadcrumb.jsx';
import Cargando from './utils/Cargando.jsx';
import { avisarExito, avisarError } from '../services/toast';
import axios from 'axios';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const esSi = (v) => String(v).toUpperCase() === 'SI';

function Grupo() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [params] = useSearchParams();
  const id = params.get('id');
  const modo = id ? 'editar' : 'crear';

  const [loading, setLoading] = useState(!!id);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    grupo: '', grudescrip: '', gruventa: true, grustock: false, gruorden: 0, generico: false,
  });
  const set = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  const cargar = useCallback(async () => {
    if (!id) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await axios.get(API_BASE_URL + GRUPO_GET, { params: { id }, headers: authHeader() });
      const g = res.data?.data;
      if (g) {
        setForm({
          grupo: g.grupo ?? '',
          grudescrip: g.grudescrip ?? '',
          gruventa: esSi(g.gruventa),
          grustock: esSi(g.grustock),
          gruorden: g.gruorden ?? 0,
          generico: Number(g.generico) === 1,
        });
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Error al cargar el grupo.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    setError('');
    if (!form.grupo || !String(form.grudescrip).trim()) {
      setError('Número de grupo y descripción son requeridos.');
      return;
    }
    setSubmitting(true);
    const payload = {
      grupo: Number(form.grupo),
      grudescrip: form.grudescrip,
      gruventa: form.gruventa ? 'SI' : 'NO',
      grustock: form.grustock ? 'SI' : 'NO',
      gruorden: Number(form.gruorden) || 0,
      generico: form.generico ? 1 : 0,
    };
    try {
      if (modo === 'editar') {
        const res = await axios.post(API_BASE_URL + GRUPO_ACTUALIZAR, { id: Number(id), ...payload }, { headers: authHeader() });
        if (!res.data?.success) throw new Error(res.data?.message || 'Error al guardar.');
        avisarExito('Grupo actualizado.');
      } else {
        const res = await axios.post(API_BASE_URL + GRUPO_ALTA, payload, { headers: authHeader() });
        if (!res.data?.success) throw new Error(res.data?.message || 'Error al guardar.');
        avisarExito('Grupo creado.');
      }
      setTimeout(() => navigate('/grupos'), 900);
    } catch (e) {
      avisarError(e.response?.data?.message || e.message || 'Error al guardar.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
        <MenuInicial /><Cargando />
      </Container>
    );
  }

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3, pb: 4 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={Category}
          titulo={modo === 'editar' ? `Editar grupo #${id}` : 'Nuevo grupo'}
          subtitulo="Grupos / rubros del catálogo"
          ayuda="El orden define en qué posición aparece este grupo en la pantalla de Registrar."
        />
        <VolverBreadcrumb to="/grupos" label="Grupos" />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" type="number" label="N° de grupo *" value={form.grupo}
                onChange={(e) => set('grupo', e.target.value)} inputProps={{ min: 1 }} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" type="number" label="Orden" value={form.gruorden}
                onChange={(e) => set('gruorden', e.target.value)} inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Descripción *" value={form.grudescrip}
                onChange={(e) => set('grudescrip', e.target.value)} />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <FormControlLabel control={<Switch checked={form.gruventa} onChange={(e) => set('gruventa', e.target.checked)} />} label="Se registra (aparece en Registrar)" />
                <FormControlLabel control={<Switch checked={form.grustock} onChange={(e) => set('grustock', e.target.checked)} />} label="Controla stock" />
                <FormControlLabel control={<Checkbox checked={form.generico} onChange={(e) => set('generico', e.target.checked)} />} label="Genérico" />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="contained" color="primary" size="large" onClick={guardar} disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Save />}>
              {submitting ? 'Guardando…' : (modo === 'editar' ? 'Guardar cambios' : 'Crear grupo')}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default Grupo;
